import React, { useCallback } from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/libraries/design/components";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { ColumnLayout } from "@/libraries/design/foundations";
import { TabDataType, TabsSection } from "@/libraries/design/patterns/tabs/src";
import { TabsSection as WacRefreshTabsSection } from "@/libraries/design/patterns/tabs/wac-refresh-tabs";
import { useWacRefresh } from "@/utils/wac-refresh/wac-refresh-context";

export interface UniformTabsProps {
    headerText?: string;
    subHeaderText?: string;
    enableRovoBorder?: boolean;
    enableResponsiveTabSize?: boolean;
    bgColor?: BgColors;
    tabSize?: "large" | "small";
}

const uniformTabsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.500"),
    width: "100%",
    overflow: "hidden"
});

const UniformTabs12Col: React.FC<UniformTabsProps> = ({
    headerText,
    subHeaderText,
    bgColor = "bgWhite",
    enableRovoBorder = false,
    enableResponsiveTabSize = false,
    tabSize = "large"
}) => {
    const isWacRefresh = useWacRefresh();

    const renderTabs: React.ComponentType<UniformSlotWrapperComponentProps> =
        useCallback(
            ({ items }) => {
                const tabsData = items.map((item, index) => {
                    const params =
                        // @ts-ignore
                        item.props?.children?.props?.data?.parameters;

                    return {
                        id: params?.id?.value ?? `tab-${index}`,
                        title: params?.title?.value,
                        iconUrl: params?.iconUrl?.value,
                        tabComponent: item
                    } as TabDataType;
                });

                if (isWacRefresh) {
                    return (
                        <WacRefreshTabsSection
                            tabsData={tabsData}
                            tabSize={tabSize}
                        />
                    );
                }

                return (
                    <TabsSection
                        alignment="center"
                        tabsData={tabsData}
                        tabSize={tabSize}
                        bgColor={bgColor}
                        enableRovoBorder={enableRovoBorder}
                        enableResponsiveTabSize={enableResponsiveTabSize}
                    />
                );
            },
            [bgColor, enableRovoBorder, enableResponsiveTabSize]
        );

    return (
        <div css={uniformTabsContainerStyles}>
            {(headerText || subHeaderText) && (
                <ColumnLayout layout="8" tabletLayout="12">
                    <ContentBlock
                        title={headerText}
                        text={subHeaderText}
                        titleAs="h2"
                        alignment={
                            isWacRefresh
                                ? {
                                      mobile: "start",
                                      tablet: "center",
                                      desktop: "center"
                                  }
                                : "center"
                        }
                    />
                </ColumnLayout>
            )}
            <UniformSlot name="tabs" wrapperComponent={renderTabs} />
        </div>
    );
};

export default UniformTabs12Col;
